import { parisDayBounds, parisDayKey } from '@flux/shared';
import { ListFilter } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Input } from '@/components/ui/input.js';

/**
 * Contrôles de filtre des onglets Abonnements (`06` §8) : même gabarit que
 * `ListToolbar` (contrôles de 36 px sur une ligne qui passe à la ligne).
 */

/** Ajoute ou retire une valeur ; liste vide = filtre retiré de l'URL. */
export function toggleFilterValue<T>(list: readonly T[], entry: T, checked: boolean): T[] | undefined {
  const next = checked ? [...list, entry] : list.filter((item) => item !== entry);
  return next.length > 0 ? next : undefined;
}

export interface MultiSelectFilterOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface MultiSelectFilterProps<T extends string> {
  /** Libellé visible du bouton (« Topic », « Statut »…). */
  label: string;
  /** Nom accessible du bouton (« Filtrer par statut »). */
  ariaLabel: string;
  options: readonly MultiSelectFilterOption<T>[];
  value: readonly T[];
  onChange: (next: T[] | undefined) => void;
}

/** Menu à cases d'un filtre à valeurs multiples, avec le nombre de valeurs retenues. */
export function MultiSelectFilter<T extends string>({
  label,
  ariaLabel,
  options,
  value,
  onChange,
}: MultiSelectFilterProps<T>): JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" aria-label={ariaLabel}>
          <ListFilter aria-hidden="true" />
          {label}
          {value.length > 0 ? <Badge tone="gray">{value.length}</Badge> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={value.includes(option.value)}
            onCheckedChange={(checked) => {
              onChange(toggleFilterValue(value, option.value, checked));
            }}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface PeriodFilterProps {
  /** ISO-8601 UTC, début du jour parisien choisi. */
  from?: string | undefined;
  /** ISO-8601 UTC, fin du jour parisien choisi. */
  to?: string | undefined;
  fromLabel: string;
  toLabel: string;
  onChange: (patch: { from?: string | undefined; to?: string | undefined }) => void;
}

/**
 * Période en jours parisiens. Stockée dans l'URL en ISO-8601 complet
 * (`dateRangeQuerySchema`) : la conversion jour → bornes du jour parisien se
 * fait à la saisie (`parisDayBounds`).
 */
export function PeriodFilter({ from, to, fromLabel, toLabel, onChange }: PeriodFilterProps): JSX.Element {
  const fromDay = from ? parisDayKey(new Date(from)) : '';
  const toDay = to ? parisDayKey(new Date(to)) : '';
  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="date"
        aria-label={fromLabel}
        className="w-40"
        value={fromDay}
        onChange={(event) => {
          const raw = event.target.value;
          onChange({ from: raw ? parisDayBounds(raw).start.toISOString() : undefined });
        }}
      />
      <span className="text-sm text-muted-foreground" aria-hidden="true">
        →
      </span>
      <Input
        type="date"
        aria-label={toLabel}
        className="w-40"
        value={toDay}
        onChange={(event) => {
          const raw = event.target.value;
          onChange({ to: raw ? parisDayBounds(raw).end.toISOString() : undefined });
        }}
      />
    </div>
  );
}
